import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, Pencil, Plus, RotateCcw, TrendingDown } from 'lucide-react';
import {
  AmRechnerBearbeiten,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  IconButton,
  formatDatum,
  formatEuro,
  toast,
  useMobil,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { KontostandHinweis } from '@/components/KontostandHinweis';
import { RunwayChart } from '@/features/overview/RunwayChart';
import { PlannedItemDialog } from './PlannedItemDialog';
import { VergleichsLeiste } from './VergleichsLeiste';
import { EntwurfsWirkungen } from './EntwurfsWirkungen';
import { categoriesApi, plannedApi, projectionApi } from '@/lib/api';
import { runwayText } from '@/lib/format';
import type { PlannedItem } from '@/types';

/**
 * Der Szenario-Simulator.
 *
 * Entwuerfe wirken nur, solange man sie hier auswaehlt — die Basis-Projektion
 * bleibt unberuehrt. Erst "fest einplanen" macht einen Posten dauerhaft
 * wirksam. Genau dieser Zwischenschritt war der Kern der Anforderung:
 * durchrechnen, bevor man sich bindet.
 *
 * Am Handy (U4: „Am Rechner bearbeiten“) zeigt die Seite die Ausgangslage, den
 * Kontoverlauf und die fest eingeplanten Posten, ohne Auswahl und Aktionen.
 */
export function PlanungPage() {
  const queryClient = useQueryClient();
  const mobil = useMobil();

  const [ausgewaehlt, setAusgewaehlt] = useState<Set<string>>(new Set());
  const [dialogOffen, setDialogOffen] = useState(false);
  const [inBearbeitung, setInBearbeitung] = useState<PlannedItem | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<{ id: string; name: string } | null>(null);

  const posten = useQuery({
    queryKey: ['planned'],
    queryFn: () => plannedApi.list(),
  });

  const kategorien = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const szenarioIds = useMemo(() => [...ausgewaehlt], [ausgewaehlt]);

  const projektion = useQuery({
    queryKey: ['projection', szenarioIds],
    queryFn: () => projectionApi.get({ scenario_ids: szenarioIds }),
  });

  // Einzelwirkungen haengen nicht an der Auswahl — sie sind gegen dieselbe
  // Basis gerechnet und aendern sich erst, wenn ein Posten dazukommt oder
  // fest eingeplant wird. Deshalb ein eigener Key ohne `szenarioIds`.
  const wirkungen = useQuery({
    queryKey: ['projection', 'impacts'],
    queryFn: () => projectionApi.impacts(),
  });

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['planned'] });
    void queryClient.invalidateQueries({ queryKey: ['projection'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const festEinplanen = useMutation({
    mutationFn: (id: string) => plannedApi.commit(id),
    onSuccess: (posten) => {
      setAusgewaehlt((vorher) => {
        const neu = new Set(vorher);
        neu.delete(posten.id);
        return neu;
      });
      toast.success(`„${posten.name}“ ist jetzt fest eingeplant`);
      aktualisieren();
    },
    onError: () => toast.error('Fest einplanen fehlgeschlagen'),
  });

  const zurueckInEntwurf = useMutation({
    mutationFn: (id: string) => plannedApi.uncommit(id),
    onSuccess: () => {
      toast.info('Zurück in den Entwurf');
      aktualisieren();
    },
    onError: () => toast.error('Zurücksetzen fehlgeschlagen'),
  });

  // Der Fehler erscheint im Bestätigungsdialog, der dafür offen bleibt.
  const loeschen = useMutation({
    mutationFn: (id: string) => plannedApi.remove(id),
    onSuccess: (_, id) => {
      toast.info('Posten gelöscht');
      setAusgewaehlt((vorher) => {
        const neu = new Set(vorher);
        neu.delete(id);
        return neu;
      });
      aktualisieren();
    },
  });

  const oeffneDialog = (posten: PlannedItem | null) => {
    setInBearbeitung(posten);
    setDialogOffen(true);
  };

  // Die Wirkungsliste kennt nur IDs — den vollstaendigen Posten fuer die
  // Vorbelegung liefert die Postenliste.
  const bearbeiteNachId = (id: string) => {
    const treffer = (posten.data ?? []).find((p) => p.id === id);
    if (treffer) oeffneDialog(treffer);
  };

  const loescheNachId = (id: string) => {
    const treffer = (posten.data ?? []).find((p) => p.id === id);
    setZuLoeschen({ id, name: treffer?.name ?? 'Posten' });
  };

  const umschalten = (id: string) => {
    setAusgewaehlt((vorher) => {
      const neu = new Set(vorher);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  };

  const fest = (posten.data ?? []).filter((p) => p.status === 'committed');
  const erledigt = (posten.data ?? []).filter((p) => p.status === 'realized');

  const wirkung = projektion.data?.impact;
  const basis = projektion.data?.base;
  const szenario = projektion.data?.scenario;

  const kategorieName = (id: string | null) => (id ? (kategorien.data ?? []).find((k) => k.id === id)?.name : undefined);

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Planung"
        subtitle="Geplante Ausgaben durchrechnen, bevor sie verbindlich werden. Entwürfe wirken nur in der Simulation."
        actions={
          !mobil && (
            <Button variant="primary" icon={Plus} onClick={() => oeffneDialog(null)}>
              Ausgabe planen
            </Button>
          )
        }
      />

      <AmRechnerBearbeiten className="md:hidden">
        Entwürfe anlegen, vergleichen und fest einplanen geht am Rechner.
      </AmRechnerBearbeiten>

      {projektion.data && (
        <KontostandHinweis stichtag={projektion.data.balance_as_of} fortgeschrieben={projektion.data.carry_forward} />
      )}

      {basis && (
        <VergleichsLeiste
          basis={basis}
          szenario={szenario}
          horizont={projektion.data?.horizon_months ?? 24}
          anzahlAusgewaehlt={ausgewaehlt.size}
          summeAusgewaehlt={wirkung?.total_amount}
        />
      )}

      {ausgewaehlt.size > 0 && wirkung && basis && (
        <Card
          icon={TrendingDown}
          title={beschreibeWirkung(wirkung.runway_delta_months, basis.runway_months, szenario?.runway_months ?? null)}
          description={`${ausgewaehlt.size} ${ausgewaehlt.size === 1 ? 'Entwurf' : 'Entwürfe'} über ${formatEuro(
            wirkung.total_amount,
            { cent: true }
          )} · ${beschreibeTiefpunkt(wirkung.min_balance_delta)}`}
          actions={
            <Button variant="ghost" size="sm" onClick={() => setAusgewaehlt(new Set())}>
              Auswahl zurücksetzen
            </Button>
          }
        />
      )}

      {basis && (
        <Card
          title="Kontoverlauf"
          actions={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 t-small text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span aria-hidden className="h-0.5 w-4 rounded-full bg-chart-1" /> Basis:{' '}
                {runwayText(basis.runway_months, basis.horizon_exceeded, projektion.data?.horizon_months)}
              </span>
              {szenario && (
                <span className="flex items-center gap-1.5">
                  <span aria-hidden className="w-4 border-t-2 border-dashed border-chart-2" />
                  Mit Szenario:{' '}
                  {runwayText(szenario.runway_months, szenario.horizon_exceeded, projektion.data?.horizon_months)}
                </span>
              )}
            </div>
          }
        >
          <RunwayChart base={basis} scenario={szenario} minBuffer={projektion.data?.min_buffer ?? '0'} height={280} />
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          title="Entwürfe im Vergleich"
          description="Was jeder Posten allein bewirkt, sortiert nach Wirkung. Anklicken übernimmt ihn in die Auswahl oben."
          className="max-md:hidden"
        >
          {wirkungen.isPending ? (
            <p className="py-6 text-center text-muted-foreground">Wirkungen werden gerechnet …</p>
          ) : (
            <EntwurfsWirkungen
              wirkungen={wirkungen.data?.impacts ?? []}
              ausgewaehlt={ausgewaehlt}
              onUmschalten={umschalten}
              onFest={(id) => festEinplanen.mutate(id)}
              onBearbeiten={bearbeiteNachId}
              onLoeschen={loescheNachId}
            />
          )}
        </Card>

        <Card title="Fest eingeplant" description="Wirkt dauerhaft in der Basis-Projektion.">
          {fest.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground">Noch nichts fest eingeplant.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {fest.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{p.name}</div>
                    <div className="t-small text-muted-foreground">
                      {formatDatum(p.due_date)}
                      {p.repeat_count ? ` · ${p.repeat_count}×` : ''}
                      {kategorieName(p.category_id) ? ` · ${kategorieName(p.category_id)}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="mr-1 font-medium tabular">{formatEuro(p.total_amount ?? p.amount, { cent: true })}</span>
                    {!mobil && (
                      <>
                        <IconButton icon={Pencil} label="Bearbeiten" variant="ghost" size="sm" onClick={() => oeffneDialog(p)} />
                        <IconButton
                          icon={RotateCcw}
                          label="Zurück in den Entwurf"
                          variant="ghost"
                          size="sm"
                          onClick={() => zurueckInEntwurf.mutate(p.id)}
                        />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {erledigt.length > 0 && (
            <div className="mt-5 border-t border-border pt-4">
              <h3 className="mb-2 t-label text-muted-foreground">Erledigt</h3>
              <ul className="space-y-1">
                {erledigt.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 text-muted-foreground">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <CheckCircle2 size={13} strokeWidth={1.75} aria-hidden className="shrink-0" />
                      <span className="truncate">{p.name}</span>
                    </span>
                    <span className="tabular">{formatEuro(p.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      {posten.data?.length === 0 && (
        <EmptyState
          icon={ClipboardList}
          title="Noch nichts geplant"
          description="Geplante Anschaffungen erscheinen hier; was sie die Runway kostet, steht sofort daneben."
        />
      )}

      <PlannedItemDialog
        offen={dialogOffen}
        onSchliessen={() => setDialogOffen(false)}
        kategorien={kategorien.data ?? []}
        onGespeichert={aktualisieren}
        posten={inBearbeitung}
      />

      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Posten löschen"
        description={zuLoeschen ? `„${zuLoeschen.name}“ wird aus der Planung entfernt.` : undefined}
        confirmLabel="Posten löschen"
        tone="danger"
        onConfirm={() => (zuLoeschen ? loeschen.mutateAsync(zuLoeschen.id) : undefined)}
      />
    </div>
  );
}

/**
 * Der Tiefpunkt in Worten.
 *
 * `min_balance_delta` ist bereits vorzeichenbehaftet: negativ heisst, der
 * Tiefpunkt sinkt. Ein davorgesetztes "sinkt um" ergaebe eine doppelte
 * Verneinung ("sinkt um -12.000 €") und liest sich wie das Gegenteil — die
 * Richtung gehoert deshalb ins Wort, nicht ins Vorzeichen.
 */
function beschreibeTiefpunkt(delta: string): string {
  const wert = Number(delta);
  if (Math.round(wert) === 0) return 'Tiefpunkt unverändert';
  const richtung = wert < 0 ? 'sinkt' : 'steigt';
  return `Tiefpunkt ${richtung} um ${formatEuro(Math.abs(wert))}`;
}

/**
 * Die Kernaussage in Worten.
 *
 * Das Delta ist nicht immer eine Zahl: reicht das Geld in der Basis über den
 * Horizont, im Szenario aber nicht mehr, gibt es keine Differenz zu nennen —
 * dann muss der Satz es trotzdem sagen.
 */
function beschreibeWirkung(delta: number | null, basisRunway: number | null, szenarioRunway: number | null): string {
  if (delta !== null && delta > 0) {
    return `Kostet ${delta} ${delta === 1 ? 'Monat' : 'Monate'} Runway`;
  }
  if (basisRunway === null && szenarioRunway !== null) {
    return `Reißt die Runway auf ${szenarioRunway} ${szenarioRunway === 1 ? 'Monat' : 'Monate'} herunter`;
  }
  if (delta === 0) {
    return 'Kostet keine Runway — passt in den laufenden Puffer';
  }
  return 'Ohne erkennbare Wirkung auf die Runway';
}
